import classNames from 'classnames';
import isEqual from 'lodash/isEqual';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import type { MultiValue } from 'react-select';

import { useGetLoggedInUser } from '@revelio/auth';

import { SelectionListControls } from '../../../../../../shared/selection-lists/components/SelectionListControls/SelectionListControls';
import {
  type Option,
  SelectionListSelect,
} from '../../../../../../shared/selection-lists/components/SelectionListSelect/SelectionListSelect';
import { shouldShowCurrentPreviousToggle } from '../../../config/currentPreviousConfig';
import { shouldSortSelectedFlatFilterOptions } from '../../../constants/filter-constants';
import { getStaticListOptions } from '../../../constants/staticFilters';
import { useEffectiveFilters } from '../../../hooks/useEffectiveFilters';
import { useTrackFilterReset } from '../../../hooks/useTrackFilterReset';
import type { StaticListFilter } from '../../../types/filters';
import { ToggleSelect } from '../../ToggleSelect';
import layout from '../filterFormLayout.module.css';
import { NotToggle } from '../NotToggle';
import styles from '../selectionListNotToggle.module.css';

interface StaticListFilterFormProps {
  filter: StaticListFilter;
  closeMenu: () => void;
  onSave: (filter: StaticListFilter) => void;
  isNotEnabled?: boolean;
  onIsNotEnabledChange?: (isNotEnabled: boolean) => void;
  showNotToggle?: boolean;
}

export const StaticListFilterForm: React.FC<StaticListFilterFormProps> = ({
  filter,
  closeMenu,
  onSave,
  isNotEnabled: isNotEnabledProp,
  onIsNotEnabledChange,
  showNotToggle = false,
}) => {
  const location = useLocation();
  const { loggedInUser } = useGetLoggedInUser();
  const trackFilterReset = useTrackFilterReset();

  // Initialize from filter.selections prop directly (same strategy as TreeFilterForm and FreeTextFilterForm)
  const [selectedOptions, setSelectedOptions] = useState<Option[]>(() => {
    return (
      filter.selections?.map((selection) => ({
        value: selection.id,
        label: selection.name,
        limit: selection.limit,
      })) || []
    );
  });
  const [isCurrent, setIsCurrent] = useState<boolean>(filter.isCurrent ?? true);
  // isNotEnabled: whether the "not" logic is currently enabled (use prop if provided, otherwise filter value, default to false)
  const isNotEnabled = isNotEnabledProp ?? filter.isNot ?? false;

  const effectiveFilters = useEffectiveFilters();
  // Sync selectedOptions from filter.selections when filter prop changes (same pattern as TreeFilterForm)
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional dependency list
  useEffect(() => {
    const optionsToShow =
      filter.selections?.map((selection) => ({
        value: selection.id,
        label: selection.name,
        limit: selection.limit,
      })) || [];

    if (!isEqual(selectedOptions, optionsToShow)) {
      setSelectedOptions(optionsToShow);
    }
  }, [filter.selections]);

  const options = getStaticListOptions({
    filterId: filter.id,
    allFilters: effectiveFilters,
    linkupPostings: loggedInUser.linkup_postings,
  });

  const isMulti = options[0]?.limit !== 1;

  const handleSelectionChange = (
    newValue: MultiValue<Option> | Option | null
  ) => {
    if (isMulti) {
      setSelectedOptions(Array.isArray(newValue) ? [...newValue] : []);
    } else {
      if (newValue && 'value' in newValue && 'label' in newValue) {
        setSelectedOptions([newValue]);
      } else {
        setSelectedOptions([]);
      }
    }
  };

  const handleSubmit = () => {
    const selections = selectedOptions.map((option) => ({
      id: option.value,
      name: option.label,
    }));

    // Use onSave prop (same strategy as TreeFilterForm and FreeTextFilterForm)
    // This handles multi-filter instance creation via FilterFormRender
    onSave({
      ...filter,
      selections,
      isCurrent,
      isNot: isNotEnabled,
    });
    closeMenu();
  };

  const handleClear = () => {
    trackFilterReset(filter, { selectionCountBefore: selectedOptions.length });
    setSelectedOptions([]);
  };

  const showToggle = shouldShowCurrentPreviousToggle(
    filter.id,
    location.pathname
  );

  const selectValue = isMulti ? selectedOptions : (selectedOptions[0] ?? null);

  return (
    <div className={layout.formStack}>
      <div
        className={classNames(
          layout.fieldStack,
          layout.notToggleAnchor,
          showNotToggle && styles.notToggleLayout
        )}
      >
        {showNotToggle && onIsNotEnabledChange && (
          <div className={styles.notToggleRow}>
            <NotToggle
              filterName={filter.name}
              isNot={isNotEnabled}
              onChange={onIsNotEnabledChange}
            />
          </div>
        )}
        <SelectionListSelect
          options={options}
          value={selectValue}
          maxMenuHeight={showNotToggle ? 180 : 220}
          onChange={handleSelectionChange}
          isMulti={isMulti}
          sortSelectedOptions={shouldSortSelectedFlatFilterOptions(filter.id)}
        />
        {showToggle && (
          <ToggleSelect
            filterName={filter.name}
            onChange={(e: number) => setIsCurrent(e === 0)}
            initialIsCurrent={isCurrent}
          />
        )}
      </div>
      <SelectionListControls
        onClear={handleClear}
        onSubmit={handleSubmit}
        onClose={closeMenu}
        submitText={(filter.selections?.length ?? 0) > 0 ? 'Update' : 'Add'}
        isSubmitDisabled={selectedOptions.length === 0}
      />
    </div>
  );
};
